import React, { useEffect, useState, useMemo } from 'react';
import {
  getUserItemPreferences,
  getMasterEquipmentDefinitions,
  getMasterEquipment,
  getUserEquipment
} from '../services/api';
import { Box, Heading, Text, Table, Thead, Tbody, Tr, Th, Td, Divider, Spinner, Flex, Button, Badge, HStack, Image, VStack, Wrap, WrapItem, Modal, ModalOverlay, ModalContent, ModalCloseButton, ModalBody, useDisclosure } from '@chakra-ui/react';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveAs } from 'file-saver';
import Encoding from 'encoding-japanese';
import { getImageUrl } from '../utils/imageUrl';

export default function ConfirmedSummary() {
  const { targetUserId } = useParams();
  const { currentUser, isFunctionEnabled } = useAuth();
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState([]);
  const [masterData, setMasterData] = useState({}); // { item_type: { item_id: masterItem } }
  const [userEquipmentData, setUserEquipmentData] = useState({}); // { item_type: { item_id: userEquipment } }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [equipmentDefinitions, setEquipmentDefinitions] = useState({});
  const [isLoadingDefinitions, setIsLoadingDefinitions] = useState(true);
  
  // モーダル関連のstate
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageAlt, setSelectedImageAlt] = useState('');

  const handleImageClick = (imageUrl, altText) => {
    setSelectedImage(imageUrl);
    setSelectedImageAlt(altText);
    onOpen();
  };

  useEffect(() => {
    const fetchData = async () => {
      if (isLoadingDefinitions) return;
      setLoading(true);
      setError('');
      try {
        // 1. user_item_preferences取得
        const prefs = await getUserItemPreferences(targetUserId);
        setPreferences(prefs);

        // 2. 各設備種別のマスタデータを取得
        const masterMap = {};
        const userEquipmentMap = {};
        await Promise.all(
          Object.keys(equipmentDefinitions.equipment_types || {}).map(async key => {
            if (!equipmentDefinitions.equipment_types[key].enabled) return;
            try {
              // マスタデータの取得
              const masterList = await getMasterEquipment(key);
              const map = {};
              masterList.forEach(item => { map[item.id] = item; });
              masterMap[key] = map;

              // ユーザー登録設備の取得
              const userList = await getUserEquipment(key, targetUserId);
              const userMap = {};
              userList.forEach(item => { userMap[item.id] = item; });
              userEquipmentMap[key] = userMap;
            } catch (e) {
              masterMap[key] = {};
              userEquipmentMap[key] = {};
            }
          })
        );
        setMasterData(masterMap);
        setUserEquipmentData(userEquipmentMap);
      } catch (err) {
        setError('データ取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [targetUserId, equipmentDefinitions]);

  useEffect(() => {
    const fetchDefinitions = async () => {
      const defs = await getMasterEquipmentDefinitions();
      setEquipmentDefinitions(defs);
      setIsLoadingDefinitions(false);
    };
    fetchDefinitions();
  }, []);

  const equipmentList = useMemo(() => 
    Object.values(equipmentDefinitions.equipment_types || {}),
    [equipmentDefinitions]
  );

  // 設備種別ごとに確定アイテムをまとめる
  const summaryByType = useMemo(() => {
    const result = {};
    Object.keys(equipmentDefinitions.equipment_types || {}).map((key) => {
      result[key] = [];
    });
    preferences.forEach(pref => {
      if ((pref.is_fixed || pref.locked_at) && result[pref.item_type]) {
        // マスタ情報またはユーザー登録設備情報を紐付け
        const master = masterData[pref.item_type]?.[pref.item_id] || {};
        const userEquipment = userEquipmentData[pref.item_type]?.[pref.item_id] || {};
        if (master.id) {
          result[pref.item_type].push({ ...pref, ...master });
        }
        else if(userEquipment.id){
          result[pref.item_type].push({ ...pref, ...userEquipment });
        }
      }
    });
    return result;
  }, [preferences, masterData, userEquipmentData]);

  // 設備種別ごとの合計
  const totalByType = useMemo(() => {
    const result = {};
    for (const key in summaryByType) {
      result[key] = summaryByType[key].reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
    }
    return result;
  }, [summaryByType]);

  // 全体合計
  const grandTotal = useMemo(() => {
    return Object.values(totalByType).reduce((sum, v) => sum + v, 0);
  }, [totalByType]);

  // CSVエクスポート用関数
  const handleExportCSV = () => {
    const headers = ['設備種別', '名称', 'メーカー', '数量', '単価', 'メモ', '状態'];
    const rows = [];
    Object.keys(equipmentDefinitions.equipment_types || {}).forEach(key => {
      const typeLabel = equipmentDefinitions.equipment_types[key]?.name || key;
      (summaryByType[key] || []).forEach(item => {
        let state = '';
        if (isFunctionEnabled('lock') && item.locked_at) state = 'ロック済み';
        else if (item.is_fixed) state = '暫定';
        rows.push([
          typeLabel,
          item.name || '-',
          item.manufacturer || '-',
          item.quantity || 1,
          item.price || '-',
          item.memo || '',
          state
        ]);
      });
    });
    let csv = '';
    csv += headers.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',') + '\n';
    rows.forEach(row => {
      csv += row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',') + '\n';
    });
    const shiftJISData = Encoding.convert(Encoding.stringToCode(csv), {
      to: 'SJIS',
      from: 'UNICODE'
    });
    const blob = new Blob([new Uint8Array(shiftJISData)], { type: 'text/csv;charset=shift-jis' });
    saveAs(blob, '一覧.csv');
  };

  if (loading || isLoadingDefinitions) return <Spinner size="xl" />;
  if (error) return <Text color="red.500">{error}</Text>;

  return (
    <Flex gap={8} p={4} w="100vw" h="100%" direction="column">
      <HStack>
        <Button onClick={() => navigate(-1)} colorScheme="gray" variant="outline">← 戻る</Button>
        <Heading>選択アイテム一覧・費用合計</Heading>
      </HStack>
       <Box 
            bg="gray.100"
            p={4}
            borderRadius="md"
            w="100%"
        >
        <Text>
          暫定・ロックしたアイテムを一覧で確認できます。Excel等で確認したい場合はCSV出力をお試しください。
        </Text>
      </Box>
      <Flex align="center" justify="space-between" mb={4}>
        <Flex gap={2}>
          <Button
            bg="black"
            color="white"
            _hover={{ bg: 'gray.700' }}
            onClick={handleExportCSV}
          >
            CSV出力
          </Button>
        </Flex>
      </Flex>
      <Text fontSize="2xl" color="yellow.700" fontWeight="bold" mb={6}>
        合計: ¥{grandTotal.toLocaleString()}
      </Text>
      <Divider mb={6} />
      {Object.keys(equipmentDefinitions.equipment_types || {}).map(key => (
        <Box key={key} mb={8}>
          <Heading size="md" color="gray.700" mb={2}>{equipmentDefinitions.equipment_types[key].label}</Heading>
          <Text fontSize="lg" color="black" fontWeight="bold" mb={2}>
          {equipmentDefinitions.equipment_types[key].name} 小計: ¥{totalByType[key]?.toLocaleString() || 0}
          </Text>
          <Table size="sm" variant="simple" mb={2}>
            <Thead>
              <Tr>
                <Th>名称</Th>
                <Th>メーカー</Th>
                <Th>数量</Th>
                <Th>単価</Th>
                <Th>小計</Th>
                <Th>メモ</Th>
                <Th>状態</Th>
              </Tr>
            </Thead>
            <Tbody>
              {!summaryByType || summaryByType[key].length === 0 ? (
                <Tr><Td colSpan={7} color="gray.400">確定アイテムなし</Td></Tr>
              ) : (
                summaryByType[key].map(item => (
                  <Tr key={item.id}>
                    <Td>
                      {item.name || '-'}
                      {userEquipmentData[key]?.[item.item_id] && (
                        <Badge ml={2} colorScheme="black">ユーザー登録</Badge>
                      )}
                    </Td>
                    <Td>{item.manufacturer || '-'}</Td>
                    <Td>{item.quantity || 1}</Td>
                    <Td>¥{item.price?.toLocaleString() || '-'}</Td>
                    <Td>¥{((item.price || 0) * (item.quantity || 1)).toLocaleString()}</Td>
                    <Td>{item.memo || ''}</Td>
                    <Td>
                      {isFunctionEnabled('lock') && item.locked_at ? (
                        <Badge colorScheme="red">ロック済み</Badge>
                      ) : (
                        <Badge colorScheme="yellow">暫定</Badge>
                      )}
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
          {/* 設備画像の横スクロール表示 */}
          {summaryByType[key] && summaryByType[key].length > 0 && (
            <Box>
              <HStack 
                spacing={4} 
                overflowX="auto" 
                pb={2}
                css={{
                  '&::-webkit-scrollbar': {
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#888',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#555',
                  },
                }}
              >
                {summaryByType[key].map(item => {
                  // item.imagesが配列で存在する場合はその中のすべての画像を表示
                  if (item.images && Array.isArray(item.images) && item.images.length > 0) {
                    return item.images.map((imageUrl, imageIndex) => {
                      const fullImageUrl = getImageUrl(imageUrl);
                      return (
                        <Box key={`${item.id}-image-${imageIndex}`} flexShrink={0}>
                          <VStack spacing={2} align="center">
                            <Box
                              borderWidth={1}
                              borderColor="gray.200"
                              borderRadius="md"
                              overflow="hidden"
                              boxShadow="sm"
                              _hover={{ boxShadow: "md" }}
                              transition="box-shadow 0.2s"
                            >
                              {fullImageUrl ? (
                                <Image
                                  src={fullImageUrl}
                                  alt={`${item.name || '設備画像'} ${imageIndex + 1}`}
                                  width="150px"
                                  height="120px"
                                  objectFit="contain"
                                  cursor="pointer"
                                  onClick={() => handleImageClick(fullImageUrl, `${item.name || '設備画像'} ${imageIndex + 1}`)}
                                  fallback={
                                    <Box 
                                      width="150px" 
                                      height="120px" 
                                      bg="gray.100" 
                                      display="flex" 
                                      alignItems="center" 
                                      justifyContent="center"
                                    >
                                      <Text fontSize="sm" color="gray.500">画像なし</Text>
                                    </Box>
                                  }
                                />
                              ) : (
                                <Box 
                                  width="150px" 
                                  height="120px" 
                                  bg="gray.100" 
                                  display="flex" 
                                  alignItems="center" 
                                  justifyContent="center"
                                >
                                  <Text fontSize="sm" color="gray.500">画像なし</Text>
                                </Box>
                              )}
                            </Box>
                            <Text fontSize="xs" color="gray.600" textAlign="center" maxW="150px" noOfLines={2}>
                              {item.name || '名称なし'}
                              {item.images.length > 1 && (
                                <Text as="span" fontSize="xs" color="gray.400" ml={1}>
                                  ({imageIndex + 1}/{item.images.length})
                                </Text>
                              )}
                            </Text>
                          </VStack>
                        </Box>
                      );
                    });
                  } else {
                    // 画像がない場合の表示
                    return (
                      <Box key={`${item.id}-no-image`} flexShrink={0}>
                        <VStack spacing={2} align="center">
                          <Box
                            borderWidth={1}
                            borderColor="gray.200"
                            borderRadius="md"
                            overflow="hidden"
                            boxShadow="sm"
                          >
                            <Box 
                              width="150px" 
                              height="120px" 
                              bg="gray.100" 
                              display="flex" 
                              alignItems="center" 
                              justifyContent="center"
                            >
                              <Text fontSize="sm" color="gray.500">画像なし</Text>
                            </Box>
                          </Box>
                          <Text fontSize="xs" color="gray.600" textAlign="center" maxW="150px" noOfLines={2}>
                            {item.name || '名称なし'}
                          </Text>
                        </VStack>
                      </Box>
                    );
                  }
                })}
              </HStack>
            </Box>
          )}
        </Box>
      ))}
      
      {/* 画像モーダル */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton color="white" size="lg" />
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center">
            {selectedImage && (
              <Image
                src={selectedImage}
                alt={selectedImageAlt}
                maxW="90vw"
                maxH="90vh"
                objectFit="contain"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
} 